<template>
    <div class="is-size-6 mt-2" :class="clazzForFightEta" v-if="eta.earlyVerificationBlocked">
        <span class="icon" :class="timeIcon.color">
            <i class="fas" :class="timeIcon.name"></i>
        </span>
        <span> {{ translate("admin_verification.time_to_fight") }} {{ prettyEstimatedRelatedTimeOfFight }} </span>
        <span v-if="timeToUnblockVerification">
            ({{ translate("admin_verification.verification_possible_in") }} {{ prettyTimeToUnblockVerification }})
        </span>
    </div>
    <div class="is-size-6 mt-2" :class="clazzForQueue" v-if="eta.queueVerificationBlocked && eta.queuePosition !== null">
        <span class="icon" :class="lockIcon(eta.queueLockOpen).color">
            <i class="fas" :class="lockIcon(eta.queueLockOpen).name"></i>
        </span>
        <span>{{ translate("admin_verification.queue_position", false, [eta.queuePosition, eta.matName ?? ""]) }}</span>
        <span v-if="!eta.queueLockOpen">
            ({{ translate("admin_verification.verification_possible_at_queue_position", false, [eta.queueVerificationLimit]) }})
        </span>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../Common/translations";
    import { humanizeDuration } from "../Common/dateTimeUtils";
    import type { FightEstimatedTime } from "../Api/fightEstimatedTime";

    const props = defineProps<{
        eta: FightEstimatedTime;
    }>();

    function lockIcon(open: boolean) {
        return open ? { name: "fa-check-circle", color: "has-text-success" } : { name: "fa-times-circle", color: "has-text-danger" };
    }

    const timeIcon = computed(() => {
        if (props.eta.totalSecondsToFight === 0 && !props.eta.available) {
            return { name: "fa-minus-circle", color: "has-text-grey" };
        }

        return lockIcon(props.eta.earlyLockOpen);
    });

    const clazzForFightEta = computed(() => {
        if (!props.eta.earlyVerificationBlocked) {
            return "";
        }
        if (props.eta.totalSecondsToFight === 0 && !props.eta.available) {
            return "";
        }
        if (props.eta.totalSecondsToFight === 0) {
            return "has-text-warning";
        }

        return props.eta.earlyLockOpen ? "" : "has-text-danger has-text-weight-bold";
    });

    const clazzForQueue = computed(() => (props.eta.queueLockOpen ? "" : "has-text-danger has-text-weight-bold"));

    const estimatedTimeToFightInMinutes = computed(() => {
        if (!props.eta.totalSecondsToFight) {
            return 0;
        }

        return Math.round(props.eta.totalSecondsToFight / 60);
    });

    const timeToUnblockVerification = computed(() => {
        const totalMinutesToUnblockVerification = estimatedTimeToFightInMinutes.value - props.eta.earlyVerificationPeriod;

        if (totalMinutesToUnblockVerification <= 0) {
            return 0;
        }

        return totalMinutesToUnblockVerification;
    });

    const prettyEstimatedRelatedTimeOfFight = computed(() => {
        if (!props.eta.totalSecondsToFight && !props.eta.available) {
            return "----";
        }
        if (props.eta.totalSecondsToFight === 0) {
            return translate("admin_verification.fight_now");
        }

        return new Intl.RelativeTimeFormat(window.panelLocale, { style: "long", numeric: "auto" }).format(
            estimatedTimeToFightInMinutes.value,
            "minute",
        );
    });

    const prettyTimeToUnblockVerification = computed(() => {
        return humanizeDuration(timeToUnblockVerification.value, "minute", window.panelLocale, true);
    });
</script>
